import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { FileText } from 'lucide-react';
import type { ContractInstance } from '@/lib/api';

interface ViewContractModalProps {
  open: boolean;
  onClose: () => void;
  contractInstance: ContractInstance;
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function SignatureBlock({
  label,
  signatory,
  signatureImage,
  signedAt,
  required,
}: {
  label: string;
  signatory: string;
  signatureImage: string;
  signedAt: string | null;
  required: boolean;
}) {
  if (!required) return null;

  const isSigned = signedAt && signatureImage;

  return (
    <div className="flex-1 min-w-[200px] max-w-[280px]">
      {isSigned ? (
        <div className="text-center">
          <div className="h-20 flex items-end justify-center pb-1">
            <img
              src={signatureImage}
              alt={`${label} signature`}
              className="max-w-full max-h-full object-contain"
            />
          </div>
          <div className="border-t border-gray-400 pt-2">
            <p className="font-medium text-sm">{signatory}</p>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
            <p className="text-xs text-muted-foreground mt-1">{formatDate(signedAt)}</p>
          </div>
        </div>
      ) : (
        <div className="text-center">
          <div className="h-20 flex items-end justify-center pb-1">
            <span className="text-sm text-muted-foreground italic">Awaiting signature</span>
          </div>
          <div className="border-t border-dashed border-gray-300 pt-2">
            <p className="text-sm text-muted-foreground">&nbsp;</p>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export function ViewContractModal({
  open,
  onClose,
  contractInstance,
}: ViewContractModalProps) {
  // Parse and render the contract content
  let parsedContent = null;
  try {
    parsedContent = JSON.parse(contractInstance.cached_rendered_content);
  } catch {
    // If parsing fails, content might be plain text
  }

  const editor = useEditor({
    extensions: [StarterKit, Underline],
    content: parsedContent || contractInstance.cached_rendered_content,
    editable: false,
    editorProps: {
      attributes: {
        class: 'prose prose-sm sm:prose max-w-none focus:outline-none',
      },
    },
  });

  const hasAnySignature =
    contractInstance.business_signature_required ||
    contractInstance.counterparty_signature_required;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-3xl p-0 gap-0 max-h-[90vh] flex flex-col">
        {/* Header */}
        <DialogHeader className="px-6 py-4 border-b bg-muted/30">
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-muted-foreground" />
            Contract Agreement
          </DialogTitle>
        </DialogHeader>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Paper-like container */}
          <div className="m-4 sm:m-6 bg-white border shadow-sm rounded-lg">
            {/* Contract Content */}
            <div className="p-6 sm:p-8">
              <EditorContent editor={editor} />
            </div>

            {/* Signatures Section */}
            {hasAnySignature && (
              <>
                <Separator className="mx-6 sm:mx-8" />
                <div className="p-6 sm:p-8">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-6">
                    Signatures
                  </h3>
                  <div className="flex flex-wrap justify-around gap-8">
                    <SignatureBlock
                      label="Business"
                      signatory={contractInstance.business_signatory}
                      signatureImage={contractInstance.business_signature_image}
                      signedAt={contractInstance.business_signed_at}
                      required={contractInstance.business_signature_required}
                    />
                    <SignatureBlock
                      label="Client"
                      signatory={contractInstance.counterparty_signatory}
                      signatureImage={contractInstance.counterparty_signature_image}
                      signedAt={contractInstance.counterparty_signed_at}
                      required={contractInstance.counterparty_signature_required}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
